// Team Members data layer (Phase 4, P4-CC13a) — the SINGLE interface between the app
// and the invite/membership RPCs + tables added in
// supabase/migrations/20260729130000_company_invites.sql.
//
// Membership changes (role change, remove) are plain table writes: the existing
// company_members_manage RLS policy already permits owner/admin, and the
// protect_last_owner() trigger enforces the last-owner invariant — no RPC needed for
// those two. Everything invite-shaped (create/revoke/regenerate/accept, and reading
// the team roster with names/emails the browser can't otherwise see) goes through the
// security-definer RPCs, since that is the whole reason they exist.
//
// company_id is owned + frozen by DB triggers/RLS; we never send it.

import { supabase } from "./supabaseClient";
import { openMailto } from "./emailLinks";

function client() {
  if (!supabase) throw new Error("Supabase is not configured (check .env).");
  return supabase;
}

// Friendly sentences for the stable SQLSTATEs raised by the invite functions
// (MS001-MS006) plus the two paths an explicit check in create_invite cannot
// cover (a race past the MS003 check, and an explicit-null role). Deliberately
// DEFENSIVE about whether a code is present at all: PostgREST is expected to
// forward a custom SQLSTATE through as error.code, but "expected" is exactly
// what put two false facts in an earlier spec, so this never assumes it. When
// no recognised code is present, a generic sentence is returned — NEVER the
// raw Postgres message. A paying customer must never see database text.
// Never matched on error.message anywhere, for anything — codes are stable,
// messages are not.
const MESSAGES = {
  MS001: "This person is already a member of your company.",
  MS002: "You cannot invite yourself.",
  MS003: "There is already a pending invite for this email.",
  MS004: "You must own or administer a company to do that.",
  MS005: "Cannot remove or demote the last owner of a company.",
  MS006: "That invite is no longer available.",
  23505: "There is already a pending invite for this email.",   // concurrent-create race past the MS003 check (uq_company_invites_pending)
  23502: "Please choose a valid role.",                          // an explicit null role defeats the column default
};
const GENERIC_MESSAGE = "Something went wrong. Please try again.";

export function toError(e) {
  const code = e && e.code;
  const message = code && MESSAGES[code] ? MESSAGES[code] : GENERIC_MESSAGE;
  const err = new Error(message);
  err.code = code;
  err.cause = e;
  return err;
}

// ── Team roster ───────────────────────────────────────────────────────────────

/** Everyone in the caller's company: { user_id, email, full_name, role, created_at, email_confirmed }[]. */
export async function listMembers() {
  const { data, error } = await client().rpc("list_company_members");
  if (error) throw toError(error);
  return data || [];
}

// PostgREST does NOT raise an error when an UPDATE/DELETE matches zero rows — it
// reports success. Without .select() + this check, a write blocked by RLS or aimed
// at an already-gone userId would resolve cleanly while nothing actually changed,
// and the UI would report "Saved" for a member whose permissions never moved.
function assertRowChanged(data) {
  if (!data || data.length === 0) throw new Error("That change could not be applied. Please refresh and try again.");
}

/** Change a member's role. protect_last_owner() enforces the last-owner invariant (MS005). */
export async function changeMemberRole(userId, role) {
  const { data, error } = await client().from("company_members").update({ role }).eq("user_id", userId).select();
  if (error) throw toError(error);
  assertRowChanged(data);
}

/** Remove a member. Same last-owner protection as changeMemberRole. */
export async function removeMember(userId) {
  const { data, error } = await client().from("company_members").delete().eq("user_id", userId).select();
  if (error) throw toError(error);
  assertRowChanged(data);
}

// ── Invites ──────────────────────────────────────────────────────────────────

/** Pending/accepted/revoked invites for the caller's company, newest first. */
export async function listInvites() {
  const { data, error } = await client().from("company_invites")
    .select("id, email, role, status, expires_at, created_at, accepted_at")
    .in("status", ["pending", "accepted", "revoked"])
    .order("created_at", { ascending: false });
  if (error) throw toError(error);
  return data || [];
}

/**
 * Create an invite. Returns the plaintext token exactly once — it is never
 * stored (only its sha256 is) and cannot be recovered later; the row action
 * for an existing pending invite is regenerateInvite(), which rotates it.
 * Composing a browser link is a UI concern, not this module's — callers turn
 * the token into a link with buildInviteUrl(window.location.origin, token).
 * @returns {Promise<{id:string, token:string, expires_at:string}>}
 */
export async function createInvite(email, role) {
  const { data, error } = await client().rpc("create_invite", { p_email: email, p_role: role }).single();
  if (error) throw toError(error);
  return { id: data.id, token: data.token, expires_at: data.expires_at };
}

export async function revokeInvite(id) {
  const { error } = await client().rpc("revoke_invite", { p_invite_id: id });
  if (error) throw toError(error);
}

/** Rotates the token/expiry on a pending invite and returns the new one. */
export async function regenerateInvite(id) {
  const { data, error } = await client().rpc("regenerate_invite", { p_invite_id: id }).single();
  if (error) throw toError(error);
  return { token: data.token, expires_at: data.expires_at };
}

/** CC13b's logged-in acceptance path. Returns { ok, reason }. */
export async function acceptInvite(token) {
  const { data, error } = await client().rpc("accept_invite", { p_token: token }).single();
  if (error) throw toError(error);
  return data;
}

/** Pure — no browser global, so it works in the UI, in Node harnesses, anywhere. */
export const buildInviteUrl = (origin, token) => `${origin}/?invite=${token}`;

/** Opens the owner's own mail client with the invite pre-filled. Reuses the
 * house openMailto() pattern already in production for emailInvoice/emailQuotation. */
export function inviteMailto(email, url, companyName) {
  const subject = `You're invited to join ${companyName || "our team"} on Msquared`;
  const body =
`Hi,

You've been invited to join ${companyName || "our company"}'s workspace on Msquared.

Click the link below to create your account:
${url}

This link will expire in 7 days.

Thanks!`;
  return openMailto(email, subject, body);
}
